import Phaser from "phaser";
import {
  SegmentedTabBar,
  SEGMENTED_TAB_BAR_EVENT,
  type TabBarStyleToken,
} from "@yjsgames/phaser-ui";
import type { TextDirection } from "@yjsgames/i18n";
import { SingleSelection } from "@yjsgames/single-select";
import { FONT_FAMILY, TYPO } from "./uiTokens.js";
import { t } from "../i18n/i18n.js";
import { MULTIPLIERS, type Multiplier } from "../systems/multiBuy.js";
import { PALETTE } from "../themes/palette.js";

/** 배수 → 안정적 문자열 id (SingleSelection/SegmentedTabBar 키 + i18n 키 mult.{id}). */
export type MultiplierId = "x1" | "x10" | "x100" | "xMax";

/** Multiplier ↔ MultiplierId 양방향 매핑 ("max"는 키에 못 쓰므로 "xMax"로 치환). */
const ID_OF: Record<string, MultiplierId> = { "1": "x1", "10": "x10", "100": "x100", max: "xMax" };
/** 표시 순서 = multiBuy.MULTIPLIERS 순서 (SSoT). */
const MULT_IDS: readonly MultiplierId[] = MULTIPLIERS.map((m) => ID_OF[String(m)]);
/** Id→Multiplier 역매핑 — MULTIPLIERS + ID_OF에서 파생(별도 선언 중복 제거, SSoT 단일화). */
const MULT_OF = Object.fromEntries(
  MULTIPLIERS.map((m) => [ID_OF[String(m)], m]),
) as Record<MultiplierId, Multiplier>;

/** 배수 바 스타일 — 하단 탭바(BottomTabView)와 동일 fantasy 9-slice 에셋 차용. */
const MULT_BAR_STYLE: TabBarStyleToken = {
  activeTextureKey: "tab_button_active_9slice",
  inactiveTextureKey: "tab_button_inactive_9slice",
  border: { left: 10, top: 10, right: 10, bottom: 10 },
  labelStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textSecondary },
  labelActiveStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textBright },
  inactiveAlpha: 0.6,
  activeLabelScale: 1.06,
};

export interface MultiplierSelectorOptions {
  /** 바 전체 가로 폭 — 4세그먼트 균등 분할. */
  readonly width: number;
  /** 각 셀 높이 (기본 28). */
  readonly height?: number;
  /** 배수 변경 콜백 — 호출자가 비용/구매 가능 갱신(refresh). */
  readonly onChange: (mult: Multiplier) => void;
  /** 텍스트 방향 — RTL이면 셀 순서 미러(SegmentedTabBar에 전달). default "ltr". */
  readonly dir?: TextDirection;
}

/**
 * 배수 선택 라디오 — x1 / x10 / x100 / xMax. `SegmentedTabBar`를 래핑하고 `SingleSelection`으로
 * "정확히 하나만 활성" 상태를 캡슐화한다. Phaser 의존(시각)이지만 상태 로직은 SingleSelection 위임이라
 * 단위 테스트는 multiBuy/SingleSelection 쪽에서 커버된다.
 *
 * 도메인 무관 — 스탯 업그레이드·환생·다이아 스탯 등 멀티 구매 시스템이 공유한다.
 * 페어: multiBuy.resolvePurchase(현재 배수 → 구매 수량/총비용).
 */
export class MultiplierSelector {
  private readonly bar: SegmentedTabBar<MultiplierId>;
  private readonly selection = new SingleSelection<MultiplierId>(MULT_IDS, "x1");

  constructor(scene: Phaser.Scene, x: number, y: number, opts: MultiplierSelectorOptions) {
    this.bar = new SegmentedTabBar<MultiplierId>(scene, x, y, {
      style: MULT_BAR_STYLE,
      width: opts.width,
      height: opts.height ?? 28,
      tabs: MULT_IDS.map((id) => ({ id, label: t(`mult.${id}`) })),
      activeId: "x1",
      dir: opts.dir,
    });
    this.bar.setDepth(3);
    this.bar.on(SEGMENTED_TAB_BAR_EVENT.TabChange, (id: MultiplierId) => {
      if (!this.selection.select(id)) return;
      this.bar.setActiveTab(id); // 같은 id 재클릭은 멱등(시각 무변), 아래 onChange만 호출
      opts.onChange(MULT_OF[id]);
    });
    scene.add.existing(this.bar);
  }

  /** 현재 선택된 배수 (multiBuy.resolvePurchase에 그대로 전달). */
  current(): Multiplier {
    return MULT_OF[this.selection.active];
  }

  /** 외부에서 배수 강제 설정 (예: 저장 복원). 현재는 미사용이지만 재사용 대비 제공. */
  setActive(mult: Multiplier): void {
    const id = ID_OF[String(mult)];
    if (this.selection.select(id)) this.bar.setActiveTab(id);
  }

  /** 언어 변경 — 라벨 재표시. (x1 등 숫자 라벨이라 보통 불변이나 xMax 등 로컬라이즈 대응.) */
  applyLanguage(): void {
    this.bar.setTabLabels((id) => t(`mult.${id}`));
  }

  /** 방향 전환(RTL) — 내부 SegmentedTabBar 셀 순서 미러 재배치. */
  relayout(dir: TextDirection): void {
    this.bar.relayout(dir);
  }

  /** 레이아웃 변경 시 바 컨테이너 위치를 갱신한다. */
  setPosition(x: number, y: number): void {
    this.bar.setPosition(x, y);
  }

  setVisible(visible: boolean): void {
    this.bar.setVisible(visible);
  }
}
